import { Modal } from 'react-bootstrap';
import { FormModal, ModalFooter } from 'pages/global/FormModal';
import { Form as FForm, Formik, useFormikContext } from 'formik';
import { FieldSelect, Switch } from 'pages/publications_v2/helpers/FieldHelper';
import Constants, { Permissions } from 'config/Constants';
import { MultiVariantSelect } from 'components/Select';
import * as Yup from 'yup';
import { useEffect, useMemo, useState } from 'react';
import { useHasModuleEnabled, useHasPermission } from 'pages/global/RestrictedContent';
import HelperFunctions from 'pages/global/HelperFunctions';
import { filterDeep } from 'deepdash-es/standalone';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { useGetBaseVariant } from 'pages/documents_v2/hooks/useGetBaseVariant';
import { useTranslation } from 'react-i18next';
import { ImportCSVFileAsBlockLayoutContent } from 'pages/documents_v2/modals/ImportCSVFileAsBlockLayoutContent';
import { useGetTableTemplates } from 'hooks/useGetTableTemplates';
import { AddTemplateBlock } from 'pages/documents_v2/views/edit_area/AddTemplateBlock';
import { useEntityTemplateParameters } from 'pages/documents_v2/hooks/useEntityTemplateParameters';
import { entityTypes } from 'pages/translation/config/Constants';

export function AddBlockModal({
    hide,
    onSubmit,
    defaultBlockType,
    defaultVariants = [],
    defaultVariantOption = 'all',
    area,
    canChangeBlockType = true,
    show = false,
    canEditVariants = true,
}) {
    const isLabelModuleEnabled = useHasModuleEnabled(Constants.modules.labels);
    const isReimbursementsModuleEnabled = useHasModuleEnabled(Constants.modules.reimbursements);
    const hasReimbursementPermission = useHasPermission(Permissions.Reimbursements['Write.All']);
    const document = useGetDocument();
    const baseVariant = useGetBaseVariant();
    const { t } = useTranslation('documents');
    const restrictions = useEntityTemplateParameters(area, entityTypes.AREA, document);
    const documentRestrictions = useEntityTemplateParameters(document, entityTypes.DOCUMENT, document);

    // Flatten base variant into an array (same as the TS version)
    const variants = useMemo(() => HelperFunctions.flatten([baseVariant]), [baseVariant]);

    const blockTypes = useMemo(() => {
        let blockTypes = [];

        if (!document) {
            return [];
        }

        if (document.modelId) {
            blockTypes.push({ label: 'Template blok', value: 'template_block' });
        }

        const canOnlyUseAsWhole = documentRestrictions?.composition
            ? !documentRestrictions?.composition?.includes('canOnlyUseAsWhole')
            : true;

        const canAddFreeBlocks = canOnlyUseAsWhole && (!document.modelId || restrictions?.canAddFreeBlocks);

        if (canAddFreeBlocks) {
            blockTypes.push(
                ...[
                    {
                        label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.text'),
                        value: Constants.blockTypes.text,
                    },
                    {
                        label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.textWithExplanation'),
                        value: Constants.blockTypes.textExplanation,
                    },
                    { label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.introtext'), value: 'intro' },
                    {
                        label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.attention'),
                        value: 'attention',
                    },
                    { label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.special'), value: 'special' },
                    { label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.info'), value: 'info' },
                    { label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.action'), value: 'action' },
                ],
            );

            if (document.type !== Constants.documentType.library) {
                blockTypes.push(
                    ...[
                        {
                            label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.image'),
                            value: Constants.blockTypes.file,
                        },
                        {
                            label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.tableLayout'),
                            value: Constants.blockTypes.blockLayout,
                        },
                        {
                            label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.table'),
                            value: Constants.blockTypes.table,
                        },
                    ],
                );

                if (isLabelModuleEnabled) {
                    blockTypes.push({
                        label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.options'),
                        value: Constants.blockTypes.label,
                    });
                }

                if (isReimbursementsModuleEnabled && hasReimbursementPermission && document.reimbursementCategoryId) {
                    blockTypes.push({
                        label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.package'),
                        value: Constants.blockTypes.reimbursement,
                    });
                }
            }

            const canAddLinkedBlock = document.type !== Constants.documentType.model && !document.modelId;

            if (canAddLinkedBlock) {
                blockTypes.push({
                    label: t('document.navbar.main.editor.left.subnav.addBlock.blocks.linked'),
                    value: Constants.blockTypes.linked,
                });
            }
        }

        return blockTypes;
    }, [
        document,
        restrictions,
        documentRestrictions,
        isLabelModuleEnabled,
        isReimbursementsModuleEnabled,
        hasReimbursementPermission,
    ]);

    const variantOptions = [
        { label: t('document.navbar.main.editor.left.subnav.addBlock.variantSelect.all'), value: 'all' },
        { label: t('document.navbar.main.editor.left.subnav.addBlock.variantSelect.none'), value: 'none' },
        {
            label: t('document.navbar.main.editor.left.subnav.addBlock.variantSelect.selectVariant'),
            value: 'select',
        },
    ];

    return (
        <FormModal
            show={show}
            onHide={handleClose}
            title={t('document.navbar.main.editor.left.subnav.addBlock.addBlock')}
        >
            <Formik
                initialValues={{
                    blockType: defaultBlockType,
                    variantOption: defaultVariantOption,
                    variants: defaultVariants,
                    templateId: undefined,
                    forceReadOnly: false,
                }}
                onSubmit={handleSubmit}
                validationSchema={AddBlockModalSchema}
            >
                {({ isSubmitting, isValid, values, setSubmitting }) => (
                    <FForm autoComplete="off">
                        <Modal.Body>
                            {blockTypes.length > 1 && (
                                <FieldSelect
                                    label={`${t('document.navbar.main.editor.left.subnav.addBlock.blockType')}*`}
                                    name="blockType"
                                    options={blockTypes}
                                />
                            )}

                            {values.blockType === Constants.blockTypes.table && <SelectTableTemplate />}

                            {canEditVariants && (
                                <FieldSelect
                                    label={`${t('document.navbar.main.editor.left.subnav.addBlock.variants')}*`}
                                    name="variantOption"
                                    options={variantOptions}
                                />
                            )}

                            <SelectVariants variants={variants} defaultVariants={defaultVariants} area={area} />

                            {values.blockType === 'template_block' && (
                                <div className="pt-4">
                                    <AddTemplateBlock
                                        closeModal={hide}
                                        variants={values.variants}
                                        baseVariantId={baseVariant.id}
                                    />
                                </div>
                            )}

                            {values.blockType === Constants.blockTypes.blockLayout && canChangeBlockType && (
                                <>
                                    <Switch
                                        name="forceReadOnly"
                                        label={t(
                                            'documents:document.navbar.main.editor.left.subnav.addBlock.forceReadOnly',
                                        )}
                                    />

                                    <ImportCSVFileAsBlockLayoutContent
                                        handleClose={handleClose}
                                        values={values}
                                        setSubmitting={setSubmitting}
                                        baseVariantId={baseVariant.id}
                                        forceReadOnly={values.forceReadOnly}
                                    />
                                </>
                            )}
                        </Modal.Body>

                        {values.blockType !== 'template_block' && (
                            <ModalFooter
                                btnSubmitText={t('document.navbar.main.editor.left.subnav.addBlock.addBlock') || ''}
                                isSubmitting={isSubmitting}
                                onHide={handleClose}
                                isValid={isValid}
                            />
                        )}
                    </FForm>
                )}
            </Formik>
        </FormModal>
    );

    function handleClose() {
        hide();
    }

    function handleSubmit(values) {
        // Always add the baseVariant id
        if (values.variantOption === 'select' || values.variantOption === 'none') {
            values.variants.push(baseVariant.id);
        }

        onSubmit(values);
    }
}

function SelectVariants({ variants, defaultVariants, area }) {
    const { values, setFieldValue } = useFormikContext();
    const [firstRender, setFirstRender] = useState(true);

    useEffect(() => {
        if (firstRender) {
            setFirstRender(false);

            if (values.variantOption === 'all') {
                setFieldValue(
                    'variants',
                    variants.map((variant) => variant.id),
                );
            }
        } else {
            if (values.variantOption === 'all') {
                setFieldValue(
                    'variants',
                    variants.map((variant) => variant.id),
                );
            } else if (values.variantOption === 'select') {
                setFieldValue('variants', defaultVariants);
            } else {
                setFieldValue('variants', []);
            }
        }
    }, [values.variantOption]);

    const variantOptions = useMemo(() => {
        return filterDeep(
            variants,
            (variantValue) => {
                if (variantValue.variantGroup || !variantValue.enabled) {
                    return undefined;
                }

                return area.documentVariants.includes(variantValue.id) ? true : undefined;
            },
            {
                childrenPath: ['children'],
            },
        );
    }, [variants, area]);

    if (values.variantOption !== 'select') {
        return null;
    }

    return (
        <MultiVariantSelect
            variants={variantOptions}
            id="select-variants"
            handleChange={(newOptions) =>
                setFieldValue(
                    'variants',
                    newOptions.map((option) => option.value),
                )
            }
            defaultVariants={defaultVariants}
        />
    );
}

function SelectTableTemplate() {
    const tableTemplates = useGetTableTemplates();

    return (
        <FieldSelect
            label="Template*"
            name="templateId"
            options={HelperFunctions.prepareDropdownData(tableTemplates)}
        />
    );
}

const AddBlockModalSchema = Yup.object().shape({
    blockType: Yup.string().required(),
    variants: Yup.array()
        .of(Yup.string())
        .when('variantOption', {
            is: (variantOption) => variantOption === 'select',
            then: (schema) => schema.min(1),
        }),
    templateId: Yup.number().when('blockType', {
        is: (blockType) => blockType === Constants.blockTypes.table,
        then: (schema) => schema.required(),
    }),
});
